import { useState } from "react";
import { motion } from "framer-motion";
import { Disc3, Headphones, CheckCircle2, Send, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { toast } from "sonner";
import axios from "axios";

// Use current origin for API calls
const API = `${window.location.origin}/api`;

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const genres = [
  "House",
  "Tech House", 
  "Techno",
  "UK Garage",
  "Minimal / Deep Tech",
  "Speed Garage",
  "Other"
];

export default function DJListPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    dj_name: "",
    email: "",
    country_city: "",
    genres: [],
    where_play: "",
    instagram_link: "",
    soundcloud_link: "",
    confirmed_active_dj: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.confirmed_active_dj) {
      toast.error("Please confirm you are an active DJ");
      return;
    }

    if (formData.genres.length === 0) {
      toast.error("Please select at least one genre");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/dj-applications`, formData);
      setIsSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="bg-[#09090b] min-h-screen text-white">
        <div className="fixed inset-0 z-[9999] pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
        
        {/* Header */}
        <Header />

        <main className="max-w-2xl mx-auto px-6 py-24 text-center pt-32">
          <motion.div {...fadeUp}>
            <CheckCircle2 className="w-16 h-16 text-neon mx-auto mb-6" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-6">
              Application Received
            </h1>
            <p className="text-zinc-400 text-lg mb-4">
              Thanks for applying to the Test Press Club DJ list.
            </p>
            <p className="text-zinc-500 mb-8">
              If approved, you'll receive occasional private club records selected for DJ circulation.
            </p>
            <Link to="/">
              <Button className="bg-transparent text-neon border border-neon hover:bg-neon hover:text-black rounded-none uppercase font-bold tracking-wider px-8 py-4 transition-colors duration-300">
                Back to Site
              </Button>
            </Link>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-[#09090b] min-h-screen text-white">
      <div className="fixed inset-0 z-[9999] pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
      
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-16 md:pb-24 border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <Headphones className="w-12 h-12 text-neon mx-auto mb-6" />
            <h1 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-6">
              Join the Test Press Club<br /><span className="text-neon">DJ List</span>
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl leading-relaxed mb-4">
              Receive early club records gaining traction online. Selected tracks circulated to DJs before wider release.
            </p>
            <p className="text-zinc-500">
              Apply to join the DJ list.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Application</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">
              DJ Application Form
            </h2>
            <p className="text-zinc-500 mb-12">Fields marked with * are required.</p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="space-y-8"
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
          >
            {/* Full Name */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                Full Name *
              </label>
              <Input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                placeholder="Your full name"
                className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                data-testid="full-name-input"
              />
            </div>

            {/* DJ / Artist Name */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                DJ / Artist Name *
              </label>
              <Input
                type="text"
                name="dj_name"
                value={formData.dj_name}
                onChange={handleChange}
                required
                placeholder="Your DJ name"
                className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                data-testid="dj-name-input"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                Email Address *
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                data-testid="email-input"
              />
            </div>

            {/* Country / City */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                Country / City
              </label>
              <Input
                type="text"
                name="country_city"
                value={formData.country_city}
                onChange={handleChange}
                placeholder="e.g., London, UK"
                className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                data-testid="location-input"
              />
            </div>

            {/* Primary Genres */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-4">
                Primary Genres Played *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {genres.map((genre) => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => handleGenreToggle(genre)}
                    className={`border px-4 py-3 text-sm uppercase tracking-wider font-bold transition-all duration-300 ${
                      formData.genres.includes(genre)
                        ? "border-neon bg-neon/10 text-neon"
                        : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300"
                    }`}
                    data-testid={`genre-${genre.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            {/* Where do you play */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                Where do you play most often?
              </label>
              <Textarea
                name="where_play"
                value={formData.where_play}
                onChange={handleChange}
                placeholder="Clubs, festivals, radio shows, etc."
                rows={3}
                className="bg-transparent border-2 border-zinc-800 focus:border-neon rounded-none px-4 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white resize-none"
                data-testid="where-play-input"
              />
            </div>

            {/* Instagram */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                Instagram Profile Link
              </label>
              <Input
                type="url"
                name="instagram_link"
                value={formData.instagram_link}
                onChange={handleChange}
                placeholder="https://instagram.com/..."
                className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                data-testid="instagram-input"
              />
            </div>

            {/* SoundCloud / Mix Link */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                SoundCloud or Mix Link
              </label>
              <Input
                type="url"
                name="soundcloud_link"
                value={formData.soundcloud_link}
                onChange={handleChange}
                placeholder="https://soundcloud.com/..."
                className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                data-testid="soundcloud-input"
              />
            </div>

            {/* Confirmation Checkbox */}
            <div className="flex items-start gap-4 pt-4">
              <Checkbox
                id="confirmed_active_dj"
                name="confirmed_active_dj"
                checked={formData.confirmed_active_dj}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, confirmed_active_dj: checked }))}
                className="mt-1 border-zinc-600 data-[state=checked]:bg-neon data-[state=checked]:border-neon rounded-none"
                data-testid="confirm-checkbox"
              />
              <label htmlFor="confirmed_active_dj" className="text-zinc-400 text-sm leading-relaxed cursor-pointer">
                I confirm I am an active DJ and would like to receive occasional promo records from Test Press Club. *
              </label>
            </div>

            {/* Submit Button */}
            <div className="pt-8">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300 border border-neon hover:border-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-3">
                    <span className="animate-spin">
                      <Disc3 className="w-5 h-5" />
                    </span>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    <Send className="w-5 h-5" />
                    Join DJ List
                  </span>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
